import { castToBoolean, vSelect } from '../../util';

class RecapResponseFormController {
  constructor($scope, $element, $timeout, StaffUserService) {
    "ngInject";
    this.$scope = $scope;
    this.$element = $element;
    this.$timeout = $timeout;
    this.StaffUserService = StaffUserService;
    this._vsInited = false;
    this._changeHandlerBound = false;
  }

  $onInit() {
    // Re-render the searchable picker any time it appears and any time the
    // underlying product pool shifts. Pool mutates in place so we use
    // $watchCollection, not $watch.
    this.$scope.$watch(() => this.canAddMerchandisingResponse, () => {
      this.$timeout(() => this._refresh());
    });
    this.$scope.$watchCollection(() => this.merchandisingAdditionalProducts, () => {
      this.$timeout(() => this._refresh());
    });
  }

  /**
   * Init / refresh the VirtualSelect picker. The plugin transforms the
   * original <select> on first init: the post-init element with the same
   * id is a <div>-wrapped widget, NOT the original <select>, and setOptions
   * lives on that new element. We always lookup by id (document-wide) so
   * we work with the current element, not a stale pre-init reference.
   */
  _refresh() {
    if (!this.canAddMerchandisingResponse) return;
    if (typeof VirtualSelect === 'undefined') return;

    const id = 'merch_additional_' + this.form.id;
    let el = document.getElementById(id);
    if (!el) return;

    const options = this.merchandisingAdditionalProducts.map(p => ({
      label: p.name,
      value: String(p.id),
    }));

    if (!this._vsInited) {
      vSelect().init({
        ele: '#' + id,
        options: options,
        search: true,
        placeholder: 'Choose a product to add...',
        searchPlaceholderText: 'Search products...',
        maxWidth: '100%',
      });
      this._vsInited = true;
      // Plugin replaces / re-bases the element; re-query to get the new one.
      el = document.getElementById(id);
      if (!el) return;
    } else if (typeof el.setOptions === 'function') {
      el.setOptions(options);
      el.setValue('');
    }

    if (!this._changeHandlerBound) {
      el.addEventListener('change', () => {
        this.selectedAdditionalProductLabelsId = el.value || '';
        this.$scope.$applyAsync();
      });
      this._changeHandlerBound = true;
    }
  }

  get formResponseSet() {
    return this._formResponseSet;
  }

  set formResponseSet(formResponseSet) {
    this._formResponseSet = formResponseSet;
  }

  get formResponses() {
    return this.formResponseSet.getOrCreateFormResponses();
  }

  get form() {
    return this.formResponseSet.form;
  }

  get allowExpandCollapseAll() {
    return this.formResponseSet.form_responses.length > 1;
  }

  get canCreateResponse() {
    return this.formResponseSet.canCreateResponse;
  }

  /**
   * Merchandising-mode "add additional products" UX: visible when the form
   * was overlaid with an allow-additional flag and the additional pool is
   * non-empty. The picker lets the recapper append rows from the program's
   * broader scope beyond the activity's pre-loaded subset.
   */
  get canAddMerchandisingResponse() {
    return this.form.isProductForm
      && this.form._merchandisingAllowAdditional
      && Array.isArray(this.form._merchandisingAdditionalScope)
      && this.form._merchandisingAdditionalScope.length > 0;
  }

  get merchandisingAdditionalProducts() {
    return this.form._merchandisingAdditionalScope || [];
  }

  addMerchandisingResponse() {
    if (!this.selectedAdditionalProductLabelsId) return;
    const product = (this.form._merchandisingAdditionalScope || [])
      .find(p => String(p.id) === String(this.selectedAdditionalProductLabelsId));
    if (!product) return;
    this.formResponseSet.addMerchandisingResponse(product);
    this.selectedAdditionalProductLabelsId = '';
  }

  createResponse() {
    // Capture the most recent existing response before appending the new one,
    // so we can carry its supplier/product forward when the rep opted in.
    const responses = this.formResponseSet.form_responses || [];
    const previous = responses.length ? responses[responses.length - 1] : null;

    const response = this.formResponseSet.createResponse();

    if (
      response
      && previous
      && this.form && this.form.isMerchandisingForm
      && this._carryOverSupplierProductEnabled()
    ) {
      // Mirror how the per-instance pickers store a selection: supplier on
      // customers_id, product as both the resolved object and the raw id (so
      // the product_labels_id getter resolves either way).
      response.customers_id = previous.customers_id || null;
      response.product = previous.product || null;
      response._raw_product_labels_id = previous.product_labels_id || null;
    }
    return response;
  }

  /**
   * Per-staff "carry over supplier & product" profile preference. When on, a
   * newly added merchandising response inherits the previous response's
   * supplier + product so the rep only re-picks them when they change.
   * Defaults to false (and safely off if the profile isn't loaded).
   *
   * @returns {boolean}
   */
  _carryOverSupplierProductEnabled() {
    const user = this.StaffUserService && this.StaffUserService.currentUser;
    return !!(user && user.profile && castToBoolean(user.profile.merch_carry_over_supplier_product));
  }

  collapseAll() {
    this.$scope.$broadcast('collapseAll');
  }

  expandAll() {
    this.$scope.$broadcast('expandAll');
  }
}

export default RecapResponseFormController;
